import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { AnalysisReport } from "@/components/AnalysisReport";
import { getShared, trackShareReferralClick } from "@/lib/api";
import { Sparkles, ArrowUpRight, Check } from "lucide-react";
import { SocialShareButtons, buildAnalysisShareText } from "@/components/SocialShareButtons";

export default function SharedReport() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const d = await getShared(token);
        setData(d);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Share link not found");
      }
    })();
  }, [token]);

  const trackAndGo = (pathAndHash) => () => {
    trackShareReferralClick(token);
    // Build a URL where ?ref comes before the hash so existing URL parsers find both.
    const [path, hash] = pathAndHash.split("#");
    const href = `${path}?ref=share_${token}${hash ? `#${hash}` : ""}`;
    window.location.href = href;
  };

  if (err) {
    return (
      <div className="min-h-screen bg-[#F7F5F0] grid place-items-center">
        <div className="text-center">
          <div className="font-display text-3xl">{err}</div>
          <Link to="/" className="text-sm underline mt-3 inline-block">Go home</Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-[#F7F5F0] grid place-items-center text-[#1E392A]/60">Loading…</div>
    );
  }

  const branded = !!data.branding;

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      {/* Minimal header — hidden for branded shares so the agency's brand leads */}
      {!branded && (
        <header className="no-print sticky top-0 z-30 glass-header">
          <div className="max-w-6xl mx-auto px-6 md:px-8 h-14 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2" data-testid="shared-brand-link">
              <div className="w-7 h-7 rounded-lg bg-[#1E392A] text-[#F7F5F0] grid place-items-center">
                <Sparkles className="w-3.5 h-3.5" />
              </div>
              <span className="font-display text-lg">Plainly</span>
            </Link>
            <a
              href={`/?ref=share_${token}`}
              onClick={(e) => { e.preventDefault(); trackAndGo("/")(); }}
              className="text-xs text-[#1E392A]/60 hover:text-[#1E392A]"
              data-testid="header-referral-link"
            >
              Make your own →
            </a>
          </div>
        </header>
      )}

      <main className="max-w-6xl mx-auto px-6 md:px-8 py-10">
        <AnalysisReport analysis={data.analysis} branding={data.branding} />

        {/* Social re-share row — only on NON-branded (Plainly-branded) shares */}
        {!branded && (
          <section
            className="no-print mt-10 rounded-2xl bg-white border border-black/5 px-5 py-4 md:px-6 md:py-5 flex flex-wrap items-center justify-between gap-3"
            data-testid="share-resocial-row"
          >
            <div className="text-sm text-[#1E392A]/70">
              Found this useful? Pass it along.
            </div>
            <SocialShareButtons
              variant="subtle"
              url={typeof window !== "undefined" ? window.location.href : ""}
              text={buildAnalysisShareText(data.analysis, { ownerView: false })}
              onShare={(network) => trackShareReferralClick(token, network)}
            />
          </section>
        )}

        {/* Conversion-focused CTA — only on NON-branded (Plainly-branded) shares */}
        {!branded && (
          <section
            className="no-print mt-12 rounded-3xl bg-[#1E392A] text-[#F7F5F0] p-7 md:p-10 bento-shadow"
            data-testid="share-referral-cta"
          >
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
              <div className="md:col-span-7">
                <div className="tag-label text-[#85CDCB]">Your turn</div>
                <h2 className="font-display text-3xl md:text-4xl mt-2 leading-tight">
                  Get this kind of read-out for <em className="text-[#E27D60] not-italic">your own</em> site.
                </h2>
                <p className="text-[#F7F5F0]/75 mt-3 max-w-md">
                  Plainly turns your Google Analytics into a short, plain-English summary —
                  what changed, why, and what to try next. No dashboards. No jargon.
                </p>
                <ul className="mt-5 space-y-2 text-sm">
                  <li className="flex gap-2"><Check className="w-4 h-4 text-[#85CDCB] mt-0.5 shrink-0" /> Connect GA or drop in a CSV — 30-second setup</li>
                  <li className="flex gap-2"><Check className="w-4 h-4 text-[#85CDCB] mt-0.5 shrink-0" /> AI explains the why behind every number</li>
                  <li className="flex gap-2"><Check className="w-4 h-4 text-[#85CDCB] mt-0.5 shrink-0" /> Optional Monday-morning email — automatic</li>
                </ul>
                <div className="mt-7 flex flex-wrap gap-3">
                  <a
                    href={`/?ref=share_${token}`}
                    onClick={(e) => { e.preventDefault(); trackAndGo("/")(); }}
                    className="inline-flex items-center rounded-full bg-[#E27D60] hover:bg-[#cf6c50] text-white h-12 px-6 font-medium"
                    data-testid="share-cta-start-btn"
                  >
                    Start free <ArrowUpRight className="ml-1.5 w-4 h-4" />
                  </a>
                  <a
                    href={`/?ref=share_${token}#pricing`}
                    onClick={(e) => { e.preventDefault(); trackAndGo("/#pricing")(); }}
                    className="inline-flex items-center rounded-full h-12 px-5 text-sm font-medium border border-white/15 hover:bg-white/5"
                    data-testid="share-cta-pricing-link"
                  >
                    See pricing
                  </a>
                </div>
              </div>

              <div className="md:col-span-5">
                <div className="rounded-2xl bg-[#152b20] border border-white/10 p-5">
                  <div className="tag-label text-[#85CDCB]">A sample read-out</div>
                  <p className="font-display text-xl leading-snug mt-2">
                    "Sessions up 18% — most of the lift came from organic search on your pricing page. Keep going."
                  </p>
                  <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
                    <div className="rounded-lg bg-white/5 p-3"><div className="text-[#F7F5F0]/55">Sessions</div><div className="font-display text-lg">+18%</div></div>
                    <div className="rounded-lg bg-white/5 p-3"><div className="text-[#F7F5F0]/55">Users</div><div className="font-display text-lg">+12%</div></div>
                    <div className="rounded-lg bg-white/5 p-3"><div className="text-[#F7F5F0]/55">Bounce</div><div className="font-display text-lg">-4%</div></div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}
      </main>

      <footer className={`no-print py-8 text-center text-xs text-[#1E392A]/50 ${branded ? "" : "border-t border-black/5"}`}>
        {branded ? (
          // Tiny attribution on white-label shares — industry standard (Typeform/Calendly/Linear pattern)
          <span>
            Powered by{" "}
            <a
              href={`/?ref=share_${token}`}
              onClick={(e) => { e.preventDefault(); trackAndGo("/")(); }}
              className="underline hover:text-[#1E392A]"
              data-testid="branded-footer-attribution"
            >
              Plainly
            </a>
          </span>
        ) : (
          <span>
            Generated with Plainly ·{" "}
            <a
              href={`/?ref=share_${token}`}
              onClick={(e) => { e.preventDefault(); trackAndGo("/")(); }}
              className="underline"
              data-testid="footer-referral-link"
            >
              Create your own
            </a>
          </span>
        )}
      </footer>
    </div>
  );
}
